import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@common/components/Button';
import { BlockInputField } from '@common/components/FormElements/BlockInputField';
import { Modal } from '@common/components/Modal';
import { Link } from 'shared-routing';
import {
  ForgottenPasswordLink,
  LoginMethodForm,
  LoginMethodItemContent,
  StyledForm,
  StyledLabel,
  SubmitButtonWrapper,
} from '@features/checkout/pages/CheckoutLogin/components/styled';
import Error from '@common/components/Error';

import { Routes } from '@common/constants';
import { useTranslations } from '@lang';

import { FormValues } from '../SignIn.types';
import { SignInModalFormProps } from './SignInModalForm.types';
import { ModalWrapper } from './SignInModalForm.styles';
import { EMAIL_REGEXP } from '@utils/validators';
import { getErrorFromFieldState } from '@utils/formHelpers';

const signInModalSchema = z.object({
  email: z.string().min(1, 'required').regex(EMAIL_REGEXP, 'email'),
  password: z.string().min(1, 'required'),
});

const SignInModalForm: FC<SignInModalFormProps> = ({
  isModalOpen = false,
  onSubmit,
  handleModalClose,
  loginError = null,
}) => {
  const { t } = useTranslations();

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(signInModalSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');

  return (
    <Modal
      isOpen={isModalOpen}
      onBackgroundClick={handleModalClose}
      onEscapeKeydown={handleModalClose}
    >
      <ModalWrapper>
        <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate>
          <LoginMethodItemContent>
            <StyledLabel htmlFor="sign-in">{t('secureCheckout.signIn')}</StyledLabel>

            {loginError && <Error error={{ code: loginError }} />}

            <LoginMethodForm>
              <BlockInputField
                isRequired
                name="email"
                label={t('secureCheckout.email')}
                value={emailValue}
                onChange={(e) => setValue('email', e.target.value, { shouldValidate: true })}
                error={getErrorFromFieldState(errors.email)}
              />
              <BlockInputField
                isRequired
                type="password"
                name="password"
                label={t('secureCheckout.password')}
                value={passwordValue}
                onChange={(e) => setValue('password', e.target.value, { shouldValidate: true })}
                error={getErrorFromFieldState(errors.password)}
              />
              <ForgottenPasswordLink>
                <Link route={Routes.FORGOTTEN_PASSWORD}>
                  {t('secureCheckout.forgotYourPassword')}
                </Link>
              </ForgottenPasswordLink>
              <SubmitButtonWrapper>
                <Button isPrimary>{t('secureCheckout.signIn')}</Button>
              </SubmitButtonWrapper>
            </LoginMethodForm>
          </LoginMethodItemContent>
        </StyledForm>
      </ModalWrapper>
    </Modal>
  );
};

export default SignInModalForm;
